import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { HelpCircle, ExternalLink, Copy, Check, Monitor, Settings, Play, Lightbulb } from "lucide-react";

export function OBSTutorialModal() {
  const [copied, setCopied] = useState(false);
  
  const exampleUrl = "https://seuapp.com/translation?mode=obs&bg=transparent&fontSize=48&color=ffffff&position=bottom&shadow=true";
  
  const copyExample = () => {
    navigator.clipboard.writeText(exampleUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="border-purple-500/50 text-purple-300 hover:bg-purple-500/20 hover:text-purple-200"
        >
          <HelpCircle className="w-4 h-4 mr-2" />
          Passo a passo OBS
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-zinc-900 border-zinc-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-white flex items-center gap-2">
            <Monitor className="w-6 h-6 text-purple-400" />
            Como usar no OBS Studio
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 mt-4">
          {/* Step 1 */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-sm">1</span>
              Gerar a URL no App
            </h3>
            <div className="pl-9 space-y-2 text-zinc-300 text-sm">
              <p>1. Na seção <strong>"Integração com OBS"</strong> acima, personalize:</p>
              <ul className="list-disc pl-5 space-y-1 text-zinc-400">
                <li><strong>Cor de Fundo:</strong> Transparente (recomendado para overlay)</li>
                <li><strong>Tamanho da Fonte:</strong> 48px+ para transmissões em 1080p</li>
                <li><strong>Cor do Texto:</strong> Branco ou amarelo para boa visibilidade</li>
                <li><strong>Posição:</strong> Inferior (mais comum para legendas)</li>
                <li><strong>Sombra:</strong> Ative para melhor legibilidade</li>
              </ul>
              <p>2. Clique em <strong>"Gerar URL para OBS"</strong></p>
              <p>3. <strong>Copie a URL</strong> gerada</p>
            </div>
          </div>

          {/* Step 2 */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-sm">2</span>
              Configurar no OBS
            </h3>
            <div className="pl-9 space-y-2 text-zinc-300 text-sm">
              <p>1. Abra o <strong>OBS Studio</strong></p>
              <p>2. Na área de <strong>Fontes</strong> (Sources), clique no <strong>+</strong></p>
              <p>3. Selecione <strong>"Navegador"</strong> (Browser Source)</p>
              <p>4. Dê um nome (ex: "Legendas Tradução")</p>
              <p>5. Configure:</p>
              <div className="bg-zinc-800 rounded-lg p-3 mt-2 space-y-2">
                <div className="flex justify-between">
                  <span className="text-zinc-400">URL:</span>
                  <span className="text-purple-300">Cole a URL copiada do app</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Largura:</span>
                  <span className="text-white">1920</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-zinc-400">Altura:</span>
                  <span className="text-white">200 a 400</span>
                </div>
                <div className="flex items-center gap-2 text-green-400">
                  <Check className="w-4 h-4" />
                  <span>Desligar fonte quando não visível</span>
                </div>
                <div className="flex items-center gap-2 text-green-400">
                  <Check className="w-4 h-4" />
                  <span>Atualizar navegador quando a cena ficar ativa</span>
                </div>
              </div>
              <p>6. Clique <strong>OK</strong></p>
            </div>
          </div>

          {/* Step 3 */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-sm">3</span>
              Posicionar a Legenda
            </h3>
            <div className="pl-9 space-y-2 text-zinc-300 text-sm">
              <p>1. Arraste a fonte para a posição desejada na tela</p>
              <p>2. Redimensione conforme necessário</p>
              <p>3. Se escolheu fundo <strong>Transparente</strong>, a legenda ficará sobre seu vídeo/jogo</p>
            </div>
          </div>

          {/* Step 4 */}
          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-purple-400 flex items-center gap-2">
              <span className="w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center text-sm">4</span>
              Iniciar a Tradução
            </h3>
            <div className="pl-9 space-y-2 text-zinc-300 text-sm">
              <p>1. No app (Dashboard), clique em <strong>"Start"</strong> para iniciar a gravação</p>
              <p>2. As traduções aparecerão automaticamente no OBS em tempo real!</p>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4 space-y-3">
            <h3 className="text-lg font-semibold text-purple-300 flex items-center gap-2">
              <Lightbulb className="w-5 h-5" />
              Dicas Importantes
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div className="space-y-1">
                <p className="text-purple-300 font-medium">Fundo Transparente</p>
                <p className="text-zinc-400">Use para sobrepor sobre vídeos/jogos</p>
              </div>
              <div className="space-y-1">
                <p className="text-purple-300 font-medium">Fundo Verde/Azul</p>
                <p className="text-zinc-400">Use com filtro Chroma Key para mais controle</p>
              </div>
              <div className="space-y-1">
                <p className="text-purple-300 font-medium">Sombra no Texto</p>
                <p className="text-zinc-400">Sempre ative quando usar fundo transparente</p>
              </div>
              <div className="space-y-1">
                <p className="text-purple-300 font-medium">Segunda Janela</p>
                <p className="text-zinc-400">Crie duas Browser Sources para idiomas diferentes</p>
              </div>
            </div>
          </div>

          {/* Example URL */}
          <div className="space-y-2">
            <p className="text-sm text-zinc-400">Exemplo de URL gerada:</p>
            <div className="flex items-center gap-2 bg-zinc-800 rounded-lg p-3">
              <code className="flex-1 text-xs text-purple-300 break-all">
                {exampleUrl}
              </code>
              <Button
                size="sm"
                variant="ghost"
                onClick={copyExample}
                className="h-8 w-8 p-0 text-purple-400 hover:text-purple-300 flex-shrink-0"
              >
                {copied ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* OBS Download Link */}
          <div className="pt-2 border-t border-zinc-700">
            <a 
              href="https://obsproject.com/download" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-purple-400 hover:text-purple-300"
            >
              <ExternalLink className="w-4 h-4" />
              Baixar OBS Studio (gratuito)
            </a>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
